import { useEffect, useState } from 'react'

import type { Product } from '@/lib/api/product-api'
import type { SalesOrderItem } from '@/lib/api/sales-order-api'
import {
    kiotVietLineMeta,
    kiotVietLineName,
} from '@/components/orders/kiotviet-item-cell'
import { ProductPicker } from '@/components/products/product-picker'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    useKiotVietCandidates,
    useLinkKiotVietProduct,
} from '@/hooks/use-kiotviet-link-query'
import { useKiotVietCodeNames } from '@/hooks/use-kiotviet-code-names'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { kiotVietLinkedLabel } from '@/lib/kiotviet-link'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

interface Props {
    orderId: string
    item: SalesOrderItem | null
    open: boolean
    onOpenChange: (open: boolean) => void
}

/**
 * Popup "Nối sản phẩm" (M-145): chọn SP ERP cho một dòng KiotViet bằng
 * ProductPicker dùng chung — gợi ý tương đồng ghim đầu, SP đã nối có nhãn
 * "Đang nối". Chọn SP đang nối mã khác thì hỏi xác nhận trước. Không kiểm tra
 * ĐVT: nhân viên tự đối chiếu mã/tên/ĐVT KiotViet hiện ở đầu popup.
 */
export function KiotVietLinkDialog({
    orderId,
    item,
    open,
    onOpenChange,
}: Props) {
    const code = item?.kiotviet_product_code ?? ''
    const name = item?.kiotviet_product_name || item?.product_name || ''
    const { data: candidates } = useKiotVietCandidates(code, name, open)
    const link = useLinkKiotVietProduct(orderId)
    const [picked, setPicked] = useState<Product | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [confirming, setConfirming] = useState(false)
    // Mỗi lần mở popup cho một dòng = một attempt `kiotviet_item_link`. Chỉ
    // gửi enum/bucket (nguồn chọn, có xung đột nối lại, dòng có mã, số gợi ý) —
    // không gửi tên/mã/số tiền.
    const workflow = useWorkflowTracking('kiotviet_item_link', {
        active: open && item !== null,
        attemptKey: item?.id,
        operation: 'update',
    })
    useEffect(() => {
        if (open && item) workflow.setContext({ lineHasCode: Boolean(code) })
    }, [open, item, code, workflow])
    useEffect(() => {
        if (open && candidates)
            workflow.setContext({ suggestionCount: candidates.length })
    }, [open, candidates, workflow])

    const reset = () => {
        setPicked(null)
        setError(null)
        setConfirming(false)
    }
    const handleOpenChange = (next: boolean) => {
        if (!next) reset()
        onOpenChange(next)
    }

    // SP đang nối mã KHÁC: nối lại sẽ gỡ mã cũ của nó. Dòng không mã không đổi
    // mapping nên không cần hỏi.
    const otherCode =
        code && picked?.kiotviet_sku && picked.kiotviet_sku !== code
            ? picked.kiotviet_sku
            : null
    // SP chọn từ danh sách cuộn chưa kèm tên KiotViet → tra theo mã cho hộp
    // xác nhận (gợi ý đã có sẵn tên / cờ mã mất).
    const needName = Boolean(
        otherCode && !picked?.kiotviet_name && !picked?.kiotviet_missing,
    )
    const { data: pickedNames } = useKiotVietCodeNames(
        otherCode ? [otherCode] : [],
        needName,
    )
    const otherLabel = otherCode
        ? kiotVietLinkedLabel(otherCode, {
              name: picked?.kiotviet_name || pickedNames?.get(otherCode)?.name,
              missing:
                  picked?.kiotviet_missing ||
                  pickedNames?.get(otherCode)?.missing,
          })
        : null

    const handlePick = (product: Product | null) => {
        setPicked(product)
        setError(null)
        setConfirming(false)
        if (!product) return
        workflow.setContext({
            linkPick: (candidates ?? []).some((c) => c.id === product.id)
                ? 'suggested'
                : 'list',
            relinkConflict: Boolean(
                code && product.kiotviet_sku && product.kiotviet_sku !== code,
            ),
        })
    }

    const submit = async (confirmed: boolean) => {
        if (!item) return
        if (!picked) {
            workflow.step('validation_failed')
            setError(m.kiotviet_link_pick_required())
            return
        }
        if (otherCode && !confirmed) {
            workflow.step('warning_shown')
            setConfirming(true)
            return
        }
        if (otherCode) workflow.step('warning_confirmed')
        try {
            await workflow.run(() =>
                link.mutateAsync({ itemId: item.id, productId: picked.id }),
            )
        } catch {
            // Lỗi đã báo bằng toast (hook nối); giữ popup để chọn lại.
            return
        }
        handleOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle>{m.kiotviet_link_dialog_title()}</DialogTitle>
                    <DialogDescription>
                        {m.kiotviet_link_dialog_desc()}
                    </DialogDescription>
                </DialogHeader>

                {item ? (
                    <div className="rounded-md border bg-muted/40 p-3 text-sm">
                        <div className="font-medium">
                            {kiotVietLineName(item)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {kiotVietLineMeta(item)}
                        </div>
                    </div>
                ) : null}

                <ProductPicker
                    value={picked?.id ?? ''}
                    workflow={workflow}
                    onChange={(_id, product) => handlePick(product ?? null)}
                    extraProducts={candidates}
                    pinExtraFirst
                    showKiotVietLink
                />

                {error ? (
                    <p className="text-sm text-destructive">{error}</p>
                ) : null}
                {confirming && otherCode ? (
                    <p
                        className={twJoin(
                            'rounded-md border border-amber-300 bg-amber-50 p-3',
                            'text-sm text-amber-900',
                            'dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
                        )}
                    >
                        {m.kiotviet_link_relink_confirm({
                            label: otherLabel ?? otherCode,
                            code: otherCode,
                        })}
                    </p>
                ) : null}

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                    >
                        {m.kiotviet_link_cancel()}
                    </Button>
                    <Button
                        type="button"
                        // Chỉ khoá khi đang gửi (chống bấm đúp), không khoá vì
                        // chưa chọn SP — bấm thì báo lỗi rõ.
                        disabled={link.isPending}
                        onClick={() => void submit(confirming)}
                    >
                        {confirming
                            ? m.kiotviet_link_confirm_submit()
                            : m.kiotviet_link_submit()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
